import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container,
  TextField,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Autocomplete,
  ThemeProvider,
  createTheme,
  Paper,
  useMediaQuery,
  Fade,
  Grow,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';

// Create a dark theme
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#90caf9',
    },
    secondary: {
      main: '#f48fb1',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
      },
    },
  },
});

function App() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [results, setResults] = useState([]);
  const [selectedWriteup, setSelectedWriteup] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const observer = useRef();
  const isMobile = useMediaQuery('(max-width:600px)');

  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    axios.get('/api/tags')
      .then(response => setAllTags(response.data))
      .catch(error => console.error('Error fetching tags:', error));
  }, []);

  const fetchResults = useCallback(async (pageNum = 1, append = false) => {
    const loadingState = append ? setIsLoadingMore : setIsLoading;
    loadingState(true);
    
    const requestBody = {
      limit: ITEMS_PER_PAGE,
      offset: (pageNum - 1) * ITEMS_PER_PAGE,
    };
    if (searchTerm) {
      requestBody.text = searchTerm;
    }
    if (selectedTags.length > 0) {
      requestBody.tags = selectedTags;
    }

    try {
      const [searchResponse, countResponse] = await Promise.all([
        axios.post(`/api/search`, requestBody),
        axios.post(`/api/search/count`, requestBody),
      ]);

      const newResults = searchResponse.data;
      const total = countResponse.data;
      
      setTotalResults(total);
      setHasMore(pageNum * ITEMS_PER_PAGE < total);
      
      if (append) {
        setResults(prev => [...prev, ...newResults]);
      } else {
        setResults(newResults);
      }
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      loadingState(false);
    }
  }, [searchTerm, selectedTags]);

  const handleSearch = () => {
    setPage(1);
    fetchResults(1, false);
  };

  const lastResultRef = useCallback(node => {
    if (isLoadingMore || !hasMore) return;
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
        setPage(prev => {
          const nextPage = prev + 1;
          fetchResults(nextPage, true);
          return nextPage;
        });
      }
    });
    
    if (node) observer.current.observe(node);
  }, [isLoadingMore, hasMore, fetchResults]);

  const handleWriteupClick = (writeup) => {
    setSelectedWriteup(writeup);
    setOpenDialog(true);
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <Paper 
        sx={{ 
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
          py: 4,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '100%',
            background: 'radial-gradient(circle at 50% 50%, rgba(144, 202, 249, 0.1) 0%, transparent 50%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="lg">
          <Fade in timeout={1000}>
            <Box sx={{ my: 4 }}>
              <Box sx={{ 
                textAlign: 'center', 
                mb: 6,
                position: 'relative',
              }}>
                <Typography 
                  variant="h3" 
                  component="h1" 
                  sx={{ 
                    color: 'primary.main',
                    fontWeight: 700,
                    letterSpacing: '0.02em',
                    mb: 1,
                    background: 'linear-gradient(45deg, #90caf9 30%, #21CBF3 90%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    display: 'inline-block',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -8,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '60%',
                      height: 2,
                      background: 'linear-gradient(90deg, transparent, #90caf9, transparent)',
                    },
                  }}
                >
                  Inquisitionis
                </Typography>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    color: 'text.secondary',
                    mb: 4,
                  }}
                >
                  The CTF search engine
                </Typography>
              </Box>
              
              <Paper 
                elevation={3} 
                sx={{ 
                  p: 3, 
                  mb: 4,
                  borderRadius: 2,
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'stretch' : 'center',
                }}>
                  <TextField
                    fullWidth
                    label="Search writeups"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ mr: 1, color: 'primary.main' }} />,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                  <Autocomplete
                    multiple
                    options={allTags}
                    value={selectedTags}
                    onChange={(_, newValue) => setSelectedTags(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tags"
                        placeholder="Select tags"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    )}
                    sx={{ 
                      width: isMobile ? '100%' : 300,
                      '& .MuiChip-root': {
                        borderRadius: 1,
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleSearch}
                    disabled={isLoading}
                    sx={{ 
                      minWidth: 100,
                      height: 56,
                    }}
                  >
                    Search
                  </Button>
                </Box>
              </Paper>

              {isLoading ? (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  my: 4,
                }}>
                  <CircularProgress 
                    size={60} 
                    sx={{ 
                      color: 'primary.main',
                    }} 
                  />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {results.map((result, index) => (
                    <Grow in timeout={500 + index * 100} key={index}>
                      <Card ref={index === results.length - 1 ? lastResultRef : null}>
                        <CardContent>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              color: 'primary.main',
                              fontWeight: 600,
                              mb: 1,
                            }}
                          >
                            {result.ctf_name} &gt; {result.task_name}
                          </Typography>
                          <Box sx={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            gap: 1,
                            mb: 2,
                          }}>
                            {result.tags.map((tag, i) => (
                              <Chip
                                key={i}
                                label={tag}
                                size="small"
                                sx={{ 
                                  background: 'rgba(144, 202, 249, 0.1)',
                                  color: 'primary.main',
                                }}
                              />
                            ))}
                          </Box>
                          <Box sx={{ 
                            display: 'flex', 
                            gap: 2,
                            flexWrap: 'wrap',
                          }}>
                            <Button
                              variant="outlined"
                              href={`https://ctftime.org/writeup/${result.ctftime_id}`}
                              target="_blank"
                              startIcon={<OpenInNewIcon />}
                              sx={{
                                borderColor: 'primary.main',
                                color: 'primary.main',
                                '&:hover': {
                                  borderColor: 'primary.light',
                                  backgroundColor: 'rgba(144, 202, 249, 0.1)',
                                },
                              }}
                            >
                              View on CTFtime
                            </Button>
                            <Button
                              variant="contained"
                              onClick={() => handleWriteupClick(result)}
                              sx={{
                                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                                '&:hover': {
                                  background: 'linear-gradient(45deg, #1976D2 30%, #1E88E5 90%)',
                                },
                              }}
                            >
                              Read Writeup
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grow>
                  ))}
                  {isLoadingMore && (
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      my: 2,
                    }}>
                      <CircularProgress 
                        size={40} 
                        sx={{ 
                          color: 'primary.main',
                        }} 
                      />
                    </Box>
                  )}
                </Box>
              )}

              <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                  sx: {
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
                  },
                }}
              >
                <DialogTitle sx={{ 
                  color: 'primary.main',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                }}>
                  {selectedWriteup?.ctf_name} &gt; {selectedWriteup?.task_name}
                  <IconButton
                    aria-label="close"
                    onClick={() => setOpenDialog(false)}
                    sx={{ 
                      position: 'absolute', 
                      right: 8, 
                      top: 8,
                      color: 'primary.main',
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent sx={{ 
                  pt: 3,
                  '& .markdown-body': {
                    color: 'text.primary',
                    '& pre': {
                      background: 'rgba(0, 0, 0, 0.2)',
                      borderRadius: 1,
                      padding: 2,
                    },
                    '& code': {
                      background: 'rgba(0, 0, 0, 0.2)',
                      padding: '0.2em 0.4em',
                      borderRadius: 1,
                    },
                  },
                }}>
                  {selectedWriteup && (
                    <>
                      <Box sx={{ mb: 3 }}>
                        <Button
                          variant="outlined"
                          href={selectedWriteup.original_url}
                          target="_blank"
                          startIcon={<OpenInNewIcon />}
                          sx={{
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            '&:hover': {
                              borderColor: 'primary.light',
                              backgroundColor: 'rgba(144, 202, 249, 0.1)',
                            },
                          }}
                        >
                          View Original Writeup
                        </Button>
                      </Box>
                      <ReactMarkdown className="markdown-body">
                        {selectedWriteup.content}
                      </ReactMarkdown>
                    </>
                  )}
                </DialogContent>
              </Dialog>
            </Box>
          </Fade>
        </Container>
      </Paper>
    </ThemeProvider>
  );
}

export default App; 